import { isApiError } from "@core/api/api-error";
import { Program, ProgramContent, ProgramMetadata } from "@domain/program/program";
import {
	ProgramUpdateErrors,
	SessionDoneContentChangedError,
	SessionOrderChangedError,
} from "@domain/program/program-errors";
import { ProgramService } from "@domain/program/program-service";
import { Map } from "immutable";
import { observable } from "micro-observables";

export class ProgramManager {
	private readonly _programsByPatient = observable<Map<string, Map<string, Program>>>(Map());

	programsByPatient = this._programsByPatient.readOnly();

	constructor(private readonly programService: ProgramService) {}

	async fetchPatientProgram(patientId: string, programId: string): Promise<void> {
		const newProgram = await this.programService.loadPatientProgram(patientId, programId);
		this.updatePatientProgram(patientId, newProgram);
	}

	async startDraftProgram(
		patientId: string,
		programContent: ProgramContent,
		programId?: string,
	): Promise<ProgramMetadata> {
		const programMetadata = programId
			? await this.programService.startDraftProgram(patientId, programId, programContent)
			: await this.programService.startNewDraftProgram(patientId, programContent);
		this.updatePatientProgram(patientId, {
			...programContent,
			patientId,
			...programMetadata,
		});
		return programMetadata;
	}

	async createDraftProgram(
		patientId: string,
		draftProgam: ProgramContent,
		reportError?: () => boolean,
	): Promise<ProgramMetadata> {
		const programMetadata = await this.programService.createDraftProgram(patientId, draftProgam, reportError);
		this.updatePatientProgram(patientId, { ...draftProgam, patientId, ...programMetadata });
		return programMetadata;
	}

	async saveProgram(patientId: string, programId: string, programSaveData: ProgramContent): Promise<void> {
		const programMetadata = await this.programService.updateProgram(patientId, programId, programSaveData);
		this.updatePatientProgram(patientId, {
			...programSaveData,
			patientId,
			...programMetadata,
		});
	}

	async startFutureProgram(
		patientId: string,
		programId: string,
		programStartData: ProgramContent,
	): Promise<ProgramMetadata> {
		const programMetadata = await this.programService.updateProgram(patientId, programId, programStartData);
		this.updatePatientProgram(patientId, {
			patientId,
			...programStartData,
			...programMetadata,
		});
		return programMetadata;
	}

	async stopProgram(patientId: string, programId: string): Promise<ProgramMetadata> {
		const metadata = await this.programService.stopProgram(patientId, programId);
		const stoppedProgram = this._programsByPatient.get().get(patientId)?.get(programId);
		if (stoppedProgram) {
			this.updatePatientProgram(patientId, { ...stoppedProgram, ...metadata });
		}
		return metadata;
	}

	async deleteProgram(patientId: string, programId: string): Promise<void> {
		await this.programService.deleteProgram(patientId, programId);

		this._programsByPatient.update(patientMap => {
			const patientProgramsMap = patientMap.get(patientId) ?? Map();
			return patientMap.set(patientId, patientProgramsMap.remove(programId));
		});
	}

	async updateOngoingProgram(
		patientId: string,
		programId: string,
		programUpdateData: ProgramContent,
		forceChanges: boolean,
	): Promise<WithRequired<ProgramMetadata, "startDate" | "endDate">> {
		try {
			const programMetadata = await this.programService.updateProgram(
				patientId,
				programId,
				programUpdateData,
				forceChanges,
			);
			if (!programMetadata.startDate || !programMetadata.endDate) {
				throw new Error("An ongoing program should have a startDate and an endDate");
			}
			const typedMetadata = {
				...programMetadata,
				startDate: programMetadata.startDate,
				endDate: programMetadata.endDate,
			};
			this.updatePatientProgram(patientId, {
				patientId,
				...programUpdateData,
				...typedMetadata,
			});
			return typedMetadata;
		} catch (e) {
			if (isApiError(e) && e.code === ProgramUpdateErrors.SESSION_DONE_CONTENT_CHANGED) {
				throw new SessionDoneContentChangedError();
			}
			if (isApiError(e) && e.code === ProgramUpdateErrors.SESSION_ORDER_CHANGED) {
				throw new SessionOrderChangedError();
			}
			throw e;
		}
	}

	async reset(): Promise<void> {
		this._programsByPatient.set(Map());
	}

	private updatePatientProgram(patientId: string, program: Program) {
		this._programsByPatient.update(patientMap => {
			const patientProgramsMap = patientMap.get(patientId) ?? Map();
			return patientMap.set(patientId, patientProgramsMap.set(program.id, program));
		});
	}
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
